/**
 * useHealthData Hook
 * Handles API fetching, polling, and refresh logic for system health data
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { HealthData, HealthApiResponse } from '../types';
import { REFRESH_INTERVAL_MS } from '../constants';
import constants from '../../../../constants';

const API_BASE = constants.baseApiHost;
const API_KEY = constants.apikey;

// Session helpers (consistent with other API calls in the app)
const getSession = () => {
  const localStorageSession = localStorage['session'];
  return localStorageSession && localStorageSession !== 'undefined'
    ? JSON.parse(localStorageSession)
    : null;
};

const getHeaders = (): Record<string, string> => {
  const session = getSession();
  return {
    'ApiKey': API_KEY || '',
    'Content-Type': 'application/json',
    'Authorization': session?.key ? `bearer ${session.key}` : ''
  };
};

interface UseHealthDataReturn {
  data: HealthData | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  lastUpdated: Date | null;
  secondsUntilRefresh: number;
  refresh: () => void;
}

export const useHealthData = (): UseHealthDataReturn => {
  const [data, setData] = useState<HealthData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState(REFRESH_INTERVAL_MS / 1000);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const isVisibleRef = useRef(true);

  const fetchData = useCallback(async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setIsRefreshing(true);
      } else if (!data) {
        setIsLoading(true);
      }

      const response = await fetch(`${API_BASE}/health/detailed`, { 
        headers: getHeaders() 
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: HealthApiResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Unknown API error');
      }

      // DEBUG: Add random variance to latency values to demo the change animation
      // Remove this block when done testing
      const demoData = { ...result.data };
      demoData.dependencies = { ...demoData.dependencies };
      Object.keys(demoData.dependencies).forEach(key => {
        const dep = demoData.dependencies[key as keyof typeof demoData.dependencies];
        if (dep && typeof dep.latencyMs === 'number') {
          // Add ±20% random variance
          const variance = Math.round(dep.latencyMs * 0.2 * (Math.random() * 2 - 1));
          demoData.dependencies[key as keyof typeof demoData.dependencies] = {
            ...dep,
            latencyMs: Math.max(1, dep.latencyMs + variance)
          };
        }
      });

      setData(demoData);
      setError(null);
      setLastUpdated(new Date());
      setSecondsUntilRefresh(REFRESH_INTERVAL_MS / 1000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch health data';
      setError(errorMessage);
      // Don't clear existing data on error - show stale data with error indicator
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [data]);

  const refresh = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
      
      if (!document.hidden) {
        // Page became visible - refresh if we've been hidden for a while
        fetchData(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchData]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Polling interval
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      if (isVisibleRef.current) {
        fetchData();
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchData]);

  // Countdown timer
  useEffect(() => {
    countdownRef.current = setInterval(() => {
      if (isVisibleRef.current) {
        setSecondsUntilRefresh(prev => Math.max(0, prev - 1));
      }
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  return {
    data,
    isLoading,
    isRefreshing,
    error,
    lastUpdated,
    secondsUntilRefresh,
    refresh
  };
};

export default useHealthData;
